import { currentBcp47 } from "@/lib/locale-format";
import type { PricingContent, PricingPreset } from "@/lib/types";

/**
 * Pricing amounts travel as integer minor units (haléře) to match the
 * backend/invoice convention; these helpers convert for the editor UI.
 */

/** Minor units → major-unit input string ("119000" → "1190"). */
export function minorToMajorInput(minor: number): string {
  if (!Number.isFinite(minor)) return "";
  const major = minor / 100;
  // Avoid float artifacts; prices are at most 2 decimal places.
  return String(Math.round(major * 100) / 100);
}

/** Major-unit input string → minor units, or null when unparseable. */
export function majorInputToMinor(input: string): number | null {
  const clean = input.replace(/\s/g, "").replace(",", ".");
  if (clean === "") return null;
  const major = Number(clean);
  if (!Number.isFinite(major) || major < 0) return null;
  return Math.round(major * 100);
}

/** Read-only display: "1 190 CZK" (cs) / "1,190 CZK" (en). */
export function formatAmount(minor: number, currency: string): string {
  const major = minor / 100;
  const num = new Intl.NumberFormat(currentBcp47(), {
    maximumFractionDigits: 2,
  }).format(major);
  return `${num} ${currency}`;
}

/** A new preset row seeded from the tier's current default preset. */
export function createEmptyPreset(
  locales: string[],
  siblings: PricingPreset[],
): PricingPreset {
  const template = siblings.find((p) => p.isDefault) ?? siblings[0];
  const prices: Record<string, number> = {};
  for (const currency of Object.keys(template?.prices ?? {})) {
    prices[currency] = template?.prices[currency] ?? 0;
  }
  const copy: PricingPreset["copy"] = {};
  for (const locale of locales) {
    copy[locale] = { pill: "", detail: "" };
  }
  return {
    id: `p${siblings.length + 1}`,
    storageGb: template?.storageGb ?? 10,
    months: template?.months ?? 3,
    isDefault: siblings.length === 0,
    prices,
    copy,
  };
}

/**
 * Trims all strings and drops empty feature lines before sending the
 * draft to the backend (the features textarea keeps raw newlines while
 * typing).
 */
export function normalizePricingContent(content: PricingContent): PricingContent {
  const next: PricingContent = structuredClone(content);
  for (const tier of next.tiers) {
    for (const copy of Object.values(tier.copy)) {
      copy.name = copy.name.trim();
      copy.tagline = copy.tagline.trim();
      copy.cta = copy.cta.trim();
      copy.badge = copy.badge?.trim() || null;
      copy.legend = copy.legend?.trim() || null;
      copy.features = copy.features.map((f) => f.trim()).filter(Boolean);
    }
    for (const preset of tier.presets ?? []) {
      preset.id = preset.id.trim();
      for (const copy of Object.values(preset.copy)) {
        copy.pill = copy.pill.trim();
        copy.detail = copy.detail.trim();
      }
    }
  }
  return next;
}
